import * as Clipboard from 'expo-clipboard';
import { Platform } from 'react-native';

/**
 * TA-10848: on web, expo-clipboard's hasImageAsync / getImageAsync go through
 * `navigator.clipboard.read()`, which prompts for permission. When the user
 * declines, they reject with a NoPermissionException (code
 * `ERR_NO_PERMISSION`, "User denied permission to access clipboard"). ChatForm
 * calls them from the paste listener and the Ctrl/Cmd+V handler without a
 * catch, so every declined prompt became an unhandled rejection in Sentry
 * (FRONTEND-30H / -2YS / -2ZG).
 *
 * expo-clipboard maps any `NotAllowedError` to ERR_NO_PERMISSION, including
 * Chrome's "Document is not focused" (paste fired while the tab lost focus).
 * That is also "no pasted image" for us, so it is handled the same way.
 *
 * Browsers without the async clipboard read API (no `navigator.clipboard`,
 * or older Firefox with no `read()`) cannot give us an image either. They
 * are answered up front instead of calling expo-clipboard, which would throw
 * ERR_CLIPBOARD_UNAVAILABLE or a raw TypeError.
 *
 * A declined prompt or a missing API just means "no pasted image". Any other
 * failure is rethrown so a real clipboard bug still reaches Sentry.
 */
const isClipboardPermissionDenied = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { code?: unknown }).code === 'ERR_NO_PERMISSION';

const canReadClipboardImage = (): boolean =>
  Platform.OS !== 'web' ||
  typeof globalThis.navigator?.clipboard?.read === 'function';

export const hasClipboardImage = async (): Promise<boolean> => {
  if (!canReadClipboardImage()) return false;
  try {
    return await Clipboard.hasImageAsync();
  } catch (error: unknown) {
    if (isClipboardPermissionDenied(error)) return false;
    throw error;
  }
};

export const getClipboardImage = async (
  options: Clipboard.GetImageOptions
): Promise<Clipboard.ClipboardImage | null> => {
  if (!canReadClipboardImage()) return null;
  try {
    return await Clipboard.getImageAsync(options);
  } catch (error: unknown) {
    if (isClipboardPermissionDenied(error)) return null;
    throw error;
  }
};
